import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Switch } from "@/components/ui/switch"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { CommunityBanner } from "@/types/job.types"
import { useIsMobile } from "@/hooks/use-mobile"
import { Card, CardContent } from "@/components/ui/card"
import { useState, useEffect } from "react"
import { Loader2, Pencil } from "lucide-react"
import { BannerEditDialog } from "./BannerEditDialog"

interface BannerListProps {
  banners: CommunityBanner[]
  onUpdate: () => void
}

export function BannerList({ banners, onUpdate }: BannerListProps) {
  const { toast } = useToast()
  const isMobile = useIsMobile()
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({})
  const [clickCounts, setClickCounts] = useState<Record<string, number>>({})
  const [editingBanner, setEditingBanner] = useState<CommunityBanner | null>(null)

  useEffect(() => {
    if (banners.length === 0) return
    supabase
      .from("banner_clicks")
      .select("banner_id")
      .then(({ data }) => {
        if (!data) return
        const counts: Record<string, number> = {}
        data.forEach((row: { banner_id: string }) => {
          counts[row.banner_id] = (counts[row.banner_id] || 0) + 1
        })
        setClickCounts(counts)
      })
  }, [banners])

  const handleImageLoad = (bannerId: string) => {
    setLoadingImages(prev => ({ ...prev, [bannerId]: false }))
  }

  const handleImageLoadStart = (bannerId: string) => {
    setLoadingImages(prev => ({ ...prev, [bannerId]: true }))
  }

  const multipleActiveBannerTypes = ["PUBLIC_JOBS", "HOME_ADS"]

  const toggleBannerStatus = async (id: string, currentStatus: boolean) => {
    try {
      if (!currentStatus) {
        const bannerToActivate = banners.find(b => b.id === id)
        if (bannerToActivate && !multipleActiveBannerTypes.includes(bannerToActivate.type)) {
          await supabase
            .from("community_banners")
            .update({ is_active: false })
            .eq("type", bannerToActivate.type)
            .eq("display", bannerToActivate.display)
            .eq("is_active", true)
        }
      }

      const { error } = await supabase
        .from("community_banners")
        .update({ is_active: !currentStatus })
        .eq("id", id)

      if (error) throw error

      toast({
        title: "Banner atualizado",
        description: `Banner ${!currentStatus ? "ativado" : "desativado"} com sucesso!`,
      })

      onUpdate()
    } catch (error) {
      console.error("Erro ao atualizar banner:", error)
      toast({
        title: "Erro ao atualizar banner",
        description: "Ocorreu um erro ao tentar atualizar o status do banner.",
        variant: "destructive",
      })
    }
  }

  const handleDelete = async (banner: CommunityBanner) => {
    try {
      if (banner.is_active) {
        const nextBanner = banners.find(b =>
          b.type === banner.type &&
          b.display === banner.display &&
          b.id !== banner.id
        );

        if (nextBanner) {
          await supabase
            .from("community_banners")
            .update({ is_active: true })
            .eq("id", nextBanner.id);
        }
      }
      const { error } = await supabase
        .from("community_banners")
        .delete()
        .eq("id", banner.id);

      if (error) throw error;

      toast({
        title: "Banner excluído",
        description: "O banner foi excluído com sucesso!",
      });

      onUpdate();
    } catch (error) {
      console.error("Erro ao excluir banner:", error);
      toast({
        title: "Erro ao excluir banner",
        description: "Ocorreu um erro ao tentar excluir o banner.",
        variant: "destructive",
      });
    }
  };

  if (banners.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        Nenhum banner encontrado.
      </div>
    )
  }

  // Mobile view with cards
  if (isMobile) {
    return (
      <>
        <div className="space-y-4">
          {banners.map((banner) => (
            <Card key={banner.id} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="aspect-video w-full overflow-hidden rounded-md relative bg-muted">
                    {loadingImages[banner.id] && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    <img
                      src={banner.image_url || ''}
                      alt="Imagem do banner da comunidade"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width="400"
                      height="200"
                      onLoadStart={() => handleImageLoadStart(banner.id)}
                      onLoad={() => handleImageLoad(banner.id)}
                      onError={() => handleImageLoad(banner.id)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Status</p>
                      <Switch
                        checked={banner.is_active}
                        onCheckedChange={() => toggleBannerStatus(banner.id, banner.is_active)}
                      />
                    </div>
                    <div className="text-right space-y-1">
                      <p className="text-sm font-medium">Cliques</p>
                      <p className="text-sm text-muted-foreground">
                        {banner.link_url ? (clickCounts[banner.id] || 0) : "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-left space-y-1">
                      <p className="text-sm font-medium">Criado em</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(banner.created_at), "dd/MM/yyyy", { locale: ptBR })}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingBanner(banner)}
                      className="flex-1"
                    >
                      <Pencil className="h-4 w-4 mr-1" /> Editar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(banner)}
                      className="flex-1"
                    >
                      Excluir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {editingBanner && (
          <BannerEditDialog
            banner={editingBanner}
            open={!!editingBanner}
            onOpenChange={(open) => { if (!open) setEditingBanner(null) }}
            onSuccess={onUpdate}
          />
        )}
      </>
    )
  }

  // Desktop view with table
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Imagem</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Cliques</TableHead>
              <TableHead>Data de Criação</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {banners.map((banner) => (
              <TableRow key={banner.id}>
                <TableCell>
                  <div className="relative w-40 h-20 bg-muted rounded-md overflow-hidden">
                    {loadingImages[banner.id] && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    <img
                      src={banner.image_url || ''}
                      alt="Imagem do banner da comunidade"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width="160"
                      height="80"
                      onLoadStart={() => handleImageLoadStart(banner.id)}
                      onLoad={() => handleImageLoad(banner.id)}
                      onError={() => handleImageLoad(banner.id)}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={banner.is_active}
                    onCheckedChange={() => toggleBannerStatus(banner.id, banner.is_active)}
                  />
                </TableCell>
                <TableCell>
                  {banner.link_url ? (clickCounts[banner.id] || 0) : "-"}
                </TableCell>
                <TableCell>
                  {format(new Date(banner.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingBanner(banner)}
                    className="mr-2"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(banner)}
                  >
                    Excluir
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {editingBanner && (
        <BannerEditDialog
          banner={editingBanner}
          open={!!editingBanner}
          onOpenChange={(open) => { if (!open) setEditingBanner(null) }}
          onSuccess={onUpdate}
        />
      )}
    </>
  )
}
